import classNames from 'classnames';
import { PropsWithChildren, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDebounce, useOnClickOutside } from '../../hooks';
import { ReactComponent as CloseIcon } from '../../icons/close.svg';
import './Modal.scss';

interface Props {
  isOpened: boolean;
  close: () => void;
  title: string;
  className?: string;
  name?: string;
  isLoading?: boolean;
  buttons?: JSX.Element;
}

const Modal = ({
  isOpened,
  close,
  title,
  children,
  className = '',
  name,
  isLoading,
  buttons,
}: PropsWithChildren<Props>) => {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, isLoading ? () => {} : close);

  const debouncedIsOpened = useDebounce(isOpened, 250);
  const isShown = isOpened || debouncedIsOpened;

  if (!isShown) {
    return null;
  }

  return createPortal(
    <div
      className={classNames(
        'Modal__backdrop fixed-top h-100 w-100 d-flex justify-content-center align-items-center animation-forwards',
        className,
        {
          fadeIn: isOpened,
          fadeOut: !isOpened,
          'Modal__backdrop--disabled': isLoading,
        }
      )}
    >
      <div
        ref={ref}
        className={classNames(
          'Modal__box d-flex flex-column animation-forwards overflow-hidden',
          {
            fadeInDown: isOpened,
            fadeOutUp: !isOpened,
          }
        )}
        data-testid={name ? `${name}-modal` : undefined}
      >
        <div className='Modal__header py-2 px-3 d-flex align-items-center justify-content-between'>
          <div
            className='ps-3 fw-600'
            data-testid={name ? `${name}-modal-title` : undefined}
          >
            {title}
          </div>
          <div className='d-flex align-items-center'>
            {buttons}
            <button
              className='Modal__btn Modal__closeBtn d-flex align-items-center justify-content-center'
              onClick={close}
              data-testid={name ? `${name}-modal-close-btn` : undefined}
              disabled={isLoading}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className='Scrollbar Modal__inner' ref={innerRef}>
          {children}
        </div>
      </div>
    </div>,
    document.querySelector('#modals')!
  );
};

export default Modal;
